<template>
  <div>
    <h2>Create Calendar</h2>
    <p>
      Please choose times you are generally available to meet with entrepreneurs.
      You will need to confirm each meeting request, and then we'll connect you
      with the entrepreneur to finalize meeting details. All blocks are one hour long.
    </p>

    <h2>Weekly Available Times</h2>
    <b-form @submit.prevent="createMultiple">
      <b-row>
        <b-col>
          <b-form-group label="Day of the Week">
            <b-form-select name="day_of_week" v-model="day" required>
              <option value="sun">Sunday</option>
              <option value="mon">Monday</option>
              <option value="tue">Tuesday</option>
              <option value="wed">Wednesday</option>
              <option value="thu">Thursday</option>
              <option value="fri">Friday</option>
              <option value="sat">Saturday</option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group label="Start Time">
            <b-form-select name="start_time" v-model="start" required>
              <option
                :value="index"
                v-for="index in Array(24).keys()" :key="index"
              >
                {{ index | formatHour }}
              </option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group label="End Time">
            <b-form-select name="end_time" v-model="end" required>
              <option
                :value="index" :disabled="start >= index"
                v-for="index in Array(24).keys()" :key="index"
              >
                {{ index | formatHour }}
              </option>

              <option :value="24">
                {{ 24 | formatHour }}
              </option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group label="Meeting Type">
            <b-form-select name="meeting_type" v-model="type" required>
              <option value="in-person">In-Person</option>
              <option value="remote">Remote</option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col class="pt-3">
          <b-btn
            class="mt-3" type="submit" variant="secondary" block
            :disabled="day == null || start == null || end == null || type == null"
          >
            Add
          </b-btn>
        </b-col>
      </b-row>
    </b-form>

    <hr>

    <div class="d-flex justify-content-between">
      <h2>Available Times</h2>

      <div>
        <span class="swatch rounded-circle bg-primary ml-3 mr-1">&nbsp;</span> In-Person
        <span class="swatch rounded-circle bg-secondary ml-3 mr-1">&nbsp;</span> Remote
      </div>
    </div>

    <div class="text-center" v-if="loading">
      <b-spinner variant="primary">Loading...</b-spinner>
    </div>

    <div v-if="!loading">
      <table class="table table-sm text-center">
        <thead>
          <tr>
            <th>&nbsp;</th>
            <th v-for="day in days" :key="day">{{ day }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="hour in Array(24).keys()" :key="hour">
            <td>{{ hour | formatHour }} - {{ (hour + 1) | formatHour }}</td>
            <td v-for="day in days" :key="day" style="width: 11%;">
              <button
                class="btn btn-block px-1 py-0 w-100" :class="{
                  'btn-primary':   getEvent(day, hour).type === 'in-person',
                  'btn-secondary': getEvent(day, hour).type === 'remote',
                  'btn-link':      !getEvent(day, hour)
                }"
                :title="getEvent(day, hour) ? 'Remove' : 'Add'"
                @click="getEvent(day, hour) ? deleteEvent(day, hour) : addEvent(day, hour)"
              >
                <span v-if="getEvent(day, hour)">&times;</span>
                <span v-else>&plus;</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-btn
      class="float-right mt-3" variant="primary"
      to="/dashboard"
    >
      View Dashboard
    </b-btn>

    <b-modal
      id="meeting-type-modal"
      title="Meeting Type" size="sm" centered
      hide-footer no-close-on-esc no-close-on-backdrop hide-header-close
    >
      <b-btn
        class="mb-1" variant="primary" block
        @click="modalMeetingType('in-person')"
      >In-person</b-btn>

      <b-btn
        class="mb-1" variant="secondary" block
        @click="modalMeetingType('remote')"
      >Remote</b-btn>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'CalendarForm',
  data () {
    return {
      loading: true,
      days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      calendar: {},
      day: null,
      start: null,
      end: null,
      type: null
    }
  },
  mounted () {
    this.days.forEach(el => {
      this.calendar[ el.toLowerCase() ] = {}
    })
    this.load()
  },
  filters: {
    formatHour (value) {
      let hour = value % 12
      hour = hour === 0 ? 12 : hour

      return `${hour} ${value < 12 || value == 24 ? 'AM' : 'PM'}`
    }
  },
  methods: {
    load () {
      this.loading = true

      this.$http
        .get('calendar')
        .then(
          (res) => {
            this.calendar = res.data.data
          },
          () => {
            // ?
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    save () {
      this.$http
        .post('calendar', { calendar: this.calendar })
        .then(
          (res) => {
            this.calendar = res.data.data
          },
          () => {
            // ?
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    createMultiple () {
      let start = this.start
      let end = this.end
      if (start > end) {
        end = this.start
        start = this.end
      }

      for (; start < end; start += 1) {
        this.$set(this.calendar[this.day.toLowerCase()], start, {
          id: '-',
          type: this.type
        })
      }

      this.day = null
      this.start = null
      this.end = null
      this.type = null

      this.save()
    },
    getEvent (day, hour) {
      day = day.toLowerCase()
      return this.calendar[day] && this.calendar[day][hour] ? this.calendar[day][hour] : false
    },
    addEvent (day, hour) {
      this.day = day.toLowerCase()
      this.start = hour
      this.end = hour + 1

      this.$bvModal.show('meeting-type-modal')
    },
    deleteEvent (day, hour) {
      const calId = this.calendar[ day.toLowerCase() ][ hour ].id
      this.$http.delete(`calendar/${calId}`)
        .then(() => {
          this.$delete(this.calendar[ day.toLowerCase() ], hour)
          this.$forceUpdate()
        })
    },
    modalMeetingType (type) {
      this.type = type
      this.createMultiple()
      this.$bvModal.hide('meeting-type-modal')
    }
  }
}
</script>

<style scoped>
.swatch {
  display: inline-block;
  width: 20px; height: 20px;
}
</style>
